import { useEffect, useMemo } from "react";
import { Label } from "@/components/ui/label";
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from "@/components/ui/library/multi-select";
import {
  useFilterValues,
  useFilterUpdate,
} from "@/features/leads/components/leadFilter/context/FilterContext";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import {
  decodeIntakePair,
  detectIntakePairFields,
  encodeIntakePair,
  intakePairBucketKey,
  splitPairTokens,
} from "@/features/customField/utils/intakePair";
import { CF_NONE_SENTINEL } from "@/features/customField/utils/cfSentinels";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import { isHiddenKeyField, useKeyFieldIds } from "@/features/customField/api/useKeyFields";
import HighlightText from "../HighlightText";
import IntakePairFilter from "./IntakePairFilter";

interface CustomFieldFiltersProps {
  queryKey: string;
  modelName: string;
  /**
   * Leave out the KEY FIELDS (e.g. an academy's "Team") — the lead filter
   * drawer gives each one its own section (`KeyFieldSection`). Off for the
   * user / role / branch filters, which list every field.
   */
  skipKeyFields?: boolean;
  /** Render this one field only — a key field's own section. */
  onlyFieldId?: string;
}

/**
 * Selector custom fields with options; shared query cache with CustomFieldFilters.
 * A key field hidden from this viewer (server `keyFieldHidden` — "Team" for an
 * individual user) is no filter for them anywhere.
 */
export function useSelectorCustomFieldsForModel(
  queryKey: string,
  modelName: string
) {
  const { spesificFields, isLoading } = useSpesificCustoms({
    queryKey,
    modelName,
    type: "SELECTOR",
    // Filters span leads from every campaign, so campaign-scoped fields
    // (auto-created from Meta form questions) must be listed too — without
    // this only global (campaignId-null) fields appear in the sheet.
    allCampaigns: true,
    isActive: true,
  });

  const selectorFields = useMemo(
    () =>
      (spesificFields ?? []).filter(
        (f: { type: string; options?: unknown[] }) =>
          f.type === "SELECTOR" &&
          (f.options?.length ?? 0) > 0 &&
          !isHiddenKeyField(f)
      ),
    [spesificFields]
  );

  return { selectorFields, isLoading };
}

export default function CustomFieldFilters({
  queryKey,
  modelName,
  skipKeyFields = false,
  onlyFieldId,
}: CustomFieldFiltersProps) {
  const { customFieldFilters, customFieldPairs } = useFilterValues();
  const updateFilter = useFilterUpdate();
  const { selectorFields, isLoading } = useSelectorCustomFieldsForModel(
    queryKey,
    modelName
  );
  const keyFieldIds = useKeyFieldIds();

  // ── Intake Year × Intake Month correlation ─────────────────────────
  // These two fields describe ONE thing, so filtering them independently
  // produces a cross product (years [2026,2027] × months [Dec,Jan] = four
  // intakes). Once a year is picked the month field switches to a
  // year-qualified picker writing exact pairs. Detection mirrors the server's
  // `resolveIntakeCfMaps`, so both ends agree on which fields these are.
  const intakePair = useMemo(
    () => detectIntakePairFields(selectorFields),
    [selectorFields]
  );
  const yearFieldId = intakePair ? String(intakePair.yearField.id) : null;
  const monthFieldId = intakePair ? String(intakePair.monthField.id) : null;
  const pairKey = intakePair
    ? intakePairBucketKey(intakePair.yearField.id, intakePair.monthField.id)
    : null;

  // "none" is the "has no value" sentinel — it can't qualify a month by year.
  const selectedYearOptionIds = useMemo(() => {
    if (!yearFieldId) return [];
    return (customFieldFilters[yearFieldId] ?? "")
      .split(",")
      .filter((v) => v && v !== CF_NONE_SENTINEL);
  }, [customFieldFilters, yearFieldId]);

  const pairTokens = useMemo(
    () => (pairKey ? splitPairTokens(customFieldPairs?.[pairKey]) : []),
    [customFieldPairs, pairKey]
  );

  const pairsMode = Boolean(intakePair) && selectedYearOptionIds.length > 0;

  // In pair mode the month field's own entry carries ONLY the "none" sentinel
  // ("deals with no intake month"), which no year-qualified option can express.
  const monthNoneSelected = Boolean(
    monthFieldId &&
      (customFieldFilters[monthFieldId] ?? "")
        .split(",")
        .includes(CF_NONE_SENTINEL)
  );

  // Keep the two representations coherent as the year selection changes.
  // Nothing is silently lost in either direction:
  //   • years selected → an existing plain month selection EXPANDS across those
  //     years (exactly the old cross product) and becomes pairs; pairs for
  //     de-selected years are pruned.
  //   • years cleared  → pairs COLLAPSE back to their distinct months, so the
  //     month filter survives instead of vanishing with the years.
  // In pair mode the month field's own entry is reduced to the `none` sentinel
  // (and only while no pair is picked, since the two contradict): its picker is
  // off-screen, and a hidden-but-active filter fighting the visible one is the
  // worst possible outcome.
  useEffect(() => {
    if (!intakePair || !pairKey || !monthFieldId) return;

    const plainMonths = (customFieldFilters[monthFieldId] ?? "")
      .split(",")
      .filter(Boolean);

    if (!pairsMode) {
      if (pairTokens.length === 0) return;
      const months = pairTokens
        .map((t) => decodeIntakePair(t)?.monthOptionId)
        .filter((m): m is string => Boolean(m));
      const merged = [...new Set([...plainMonths, ...months])];
      updateFilter("customFieldFilters", {
        ...customFieldFilters,
        [monthFieldId]: merged.join(","),
      });
      const nextPairs = { ...customFieldPairs };
      delete nextPairs[pairKey];
      updateFilter("customFieldPairs", nextPairs);
      return;
    }

    const yearSet = new Set(selectedYearOptionIds);
    const wantsNone = plainMonths.includes(CF_NONE_SENTINEL);
    const migratedMonths = plainMonths.filter((m) => m !== CF_NONE_SENTINEL);
    const nextTokens = new Set(
      pairTokens.filter((t) => {
        const decoded = decodeIntakePair(t);
        return decoded ? yearSet.has(decoded.yearOptionId) : false;
      })
    );
    for (const year of selectedYearOptionIds) {
      for (const month of migratedMonths) {
        nextTokens.add(encodeIntakePair(year, month));
      }
    }

    const tokensChanged =
      nextTokens.size !== pairTokens.length ||
      pairTokens.some((t) => !nextTokens.has(t));
    if (tokensChanged) {
      const next = { ...customFieldPairs };
      if (nextTokens.size > 0) next[pairKey] = [...nextTokens].join(",");
      else delete next[pairKey];
      updateFilter("customFieldPairs", next);
    }

    const nextMonthCf =
      wantsNone && nextTokens.size === 0 ? CF_NONE_SENTINEL : "";
    if ((customFieldFilters[monthFieldId] ?? "") !== nextMonthCf) {
      const nextCf = { ...customFieldFilters };
      if (nextMonthCf) nextCf[monthFieldId] = nextMonthCf;
      else delete nextCf[monthFieldId];
      updateFilter("customFieldFilters", nextCf);
    }
    // `updateFilter` is a stable dispatch wrapper; re-running on it would churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    intakePair,
    pairKey,
    monthFieldId,
    pairsMode,
    selectedYearOptionIds,
    pairTokens,
    customFieldFilters,
    customFieldPairs,
  ]);

  if (isLoading || !selectorFields.length) return null;

  const handleChange = (fieldId: string, optionIds: string[]) => {
    const next = { ...customFieldFilters, [fieldId]: optionIds.join(",") };
    updateFilter("customFieldFilters", next);
  };

  /**
   * The pair picker hands back one list mixing `none` with pair tokens. "No
   * intake month" and "December 2026" are mutually exclusive claims about the
   * same deal, so picking either clears the other rather than ANDing into a
   * filter that can never match.
   */
  const handlePairChange = (values: string[]) => {
    if (!pairKey || !monthFieldId) return;
    const hasNone = values.includes(CF_NONE_SENTINEL);
    const justPickedNone = hasNone && !monthNoneSelected;
    const tokens = justPickedNone
      ? []
      : values.filter((v) => v !== CF_NONE_SENTINEL);
    const keepNone = justPickedNone;

    const nextPairs = { ...customFieldPairs };
    if (tokens.length > 0) nextPairs[pairKey] = tokens.join(",");
    else delete nextPairs[pairKey];
    updateFilter("customFieldPairs", nextPairs);

    const nextCf = { ...customFieldFilters };
    if (keepNone) nextCf[monthFieldId] = CF_NONE_SENTINEL;
    else delete nextCf[monthFieldId];
    updateFilter("customFieldFilters", nextCf);
  };

  const renderField = (field: any) => {
    const fieldId = String(field.id);

        // Intake Month, with years selected → year-qualified pair picker.
        if (pairsMode && intakePair && fieldId === monthFieldId) {
          return (
            <IntakePairFilter
              key={field.id}
              pair={intakePair}
              selectedYearOptionIds={selectedYearOptionIds}
              value={pairTokens}
              noneSelected={monthNoneSelected}
              onChange={handlePairChange}
            />
          );
        }

        const raw = customFieldFilters[fieldId] ?? "";
        const selected = raw ? raw.split(",").filter(Boolean) : [];
        // Synthetic "No <field>" option pinned to the top. Matches leads
        // whose CF has NO scope row on the lead and on any child — useful
        // for spotting records that fell through the cracks during data
        // entry. The backend recognises the literal "none" sentinel and
        // builds a NOT-EXISTS condition; everything else is treated as
        // an optionId. See `resolveCustomFieldFilters`.
        const displayName = getCfDisplayName(field);
        const options = [
          { value: "none", label: `No ${displayName}` },
          ...field.options.map((opt: any) => ({
            value: String(opt.id),
            label: String(opt.value),
          })),
        ];
        const lcName = displayName.toLowerCase();

        // ── Option groups ─────────────────────────────────────────────
        // Options carrying a `filterGroup` render under that heading, and
        // the heading toggles the whole group at once. Ungrouped options
        // keep their flat position after the "No <field>" sentinel.
        type Opt = { value: string; label: string };
        const ungrouped: Opt[] = [];
        const groups: { name: string; options: Opt[] }[] = [];
        const groupIdx = new Map<string, number>();
        for (const opt of field.options as any[]) {
          const entry = { value: String(opt.id), label: String(opt.value) };
          const groupName = String(opt.filterGroup ?? "").trim();
          if (!groupName) {
            ungrouped.push(entry);
            continue;
          }
          const key = groupName.toLowerCase();
          let idx = groupIdx.get(key);
          if (idx === undefined) {
            idx = groups.length;
            groupIdx.set(key, idx);
            groups.push({ name: groupName, options: [] });
          }
          groups[idx].options.push(entry);
        }

        const toggleGroup = (memberIds: string[]) => {
          const cur = new Set(selected);
          const allSelected = memberIds.every((id) => cur.has(id));
          if (allSelected) memberIds.forEach((id) => cur.delete(id));
          else memberIds.forEach((id) => cur.add(id));
          handleChange(fieldId, [...cur]);
        };

        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[14px] leading-[1.35] tracking-[-0.56px] text-[#051912] font-normal mb-0 pb-1.5">
              <HighlightText text={displayName} />
            </Label>
            <MultiSelect
              value={selected}
              onValueChange={(vals) => handleChange(fieldId, vals)}
            >
              <MultiSelectTrigger className="w-full h-10 bg-[#fcfcfc] border border-[var(--color-black-2)] rounded-[12px] px-3 py-2 text-[14px] leading-[1.35] tracking-[-0.56px] text-[#505e59] font-normal shadow-none hover:bg-[#fcfcfc]">
                <MultiSelectValue
                  placeholder={`Select ${lcName}`}
                  options={options}
                />
              </MultiSelectTrigger>
              <MultiSelectContent searchPlaceholder={`Search ${lcName}...`}>
                <MultiSelectItem value="none">{`No ${displayName}`}</MultiSelectItem>
                {ungrouped.map((opt) => (
                  <MultiSelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MultiSelectItem>
                ))}
                {groups.map((g) => {
                  const memberIds = g.options.map((o) => o.value);
                  const selectedCount = memberIds.filter((id) =>
                    selected.includes(id),
                  ).length;
                  const allSelected =
                    memberIds.length > 0 && selectedCount === memberIds.length;
                  return (
                    <div key={g.name.toLowerCase()}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(memberIds)}
                        title={
                          allSelected
                            ? `Clear all in ${g.name}`
                            : `Select all in ${g.name}`
                        }
                        className={`mt-1 flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-[12px] font-medium uppercase tracking-[0.04em] transition-colors hover:bg-accent ${
                          allSelected ? "text-[#156548]" : "text-[#505e59]"
                        }`}
                        style={{ borderRadius: ".8rem" }}
                      >
                        <span className="truncate">{g.name}</span>
                        <span className="ml-2 shrink-0 text-[11px] font-normal normal-case tracking-normal text-[#9ba3a0]">
                          {selectedCount > 0
                            ? `${selectedCount}/${memberIds.length}`
                            : `${memberIds.length}`}
                        </span>
                      </button>
                      {g.options.map((opt) => (
                        <MultiSelectItem
                          key={opt.value}
                          value={opt.value}
                          className="pl-4"
                        >
                          {opt.label}
                        </MultiSelectItem>
                      ))}
                    </div>
                  );
                })}
              </MultiSelectContent>
            </MultiSelect>
          </div>
        );
  };

  // Filtered at render only: the intake pair above is detected on the full
  // list, so leaving a field out here never breaks the Year × Month picker.
  const shown = selectorFields.filter((f: { id: string | number }) => {
    const id = String(f.id);
    if (onlyFieldId != null) return id === onlyFieldId;
    return !(skipKeyFields && keyFieldIds.has(id));
  });

  return <>{shown.map(renderField)}</>;
}
